/**
 * Pill — the Claude Design `.pill`: a 23px status pill with an optional leading
 * colored dot. This is the generic primitive behind `StatusPill` (which maps a
 * known status string onto a tone); use `Pill` directly when you have a tone in
 * hand rather than a status string.
 *
 *   <Pill tone="ok">Resolved</Pill>
 *   <Pill tone="neutral" dot={false}>Draft</Pill>
 */
import type { HTMLAttributes, ReactElement } from 'react';

import { cn } from './cn.js';

export type PillTone =
  | 'neutral'
  | 'primary'
  | 'ok'
  | 'warn'
  | 'danger'
  | 'info'
  | 'outline';

export interface PillProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: PillTone;
  /** Show the leading colored dot (default true). */
  dot?: boolean;
}

/** Tone → pill surface + text + dot colors (per `.pill--*` in dash.css). */
export const pillTone: Record<PillTone, { wrap: string; dot: string }> = {
  info: { wrap: 'bg-info-soft text-info', dot: 'bg-info' },
  warn: { wrap: 'bg-warn-soft text-warn', dot: 'bg-warn' },
  ok: { wrap: 'bg-ok-soft text-ok', dot: 'bg-ok' },
  danger: { wrap: 'bg-danger-soft text-danger', dot: 'bg-danger' },
  primary: { wrap: 'bg-primary-soft text-primary', dot: 'bg-primary' },
  neutral: { wrap: 'bg-surface-3 text-muted', dot: 'bg-faint' },
  outline: { wrap: 'border border-border-strong text-muted', dot: 'bg-faint' },
};

export function Pill({
  tone = 'neutral',
  dot = true,
  className,
  children,
  ...props
}: PillProps): ReactElement {
  const styles = pillTone[tone];
  return (
    <span
      data-tone={tone}
      className={cn(
        'inline-flex h-[23px] flex-shrink-0 items-center gap-1.5 whitespace-nowrap ' +
          'rounded-full border border-transparent text-xs font-bold leading-none',
        dot ? 'pl-2 pr-[9px]' : 'px-[9px]',
        styles.wrap,
        className,
      )}
      {...props}
    >
      {dot ? (
        <span
          aria-hidden="true"
          className={cn('h-1.5 w-1.5 flex-shrink-0 rounded-full', styles.dot)}
        />
      ) : null}
      {children}
    </span>
  );
}
